// Copyright ©️ 2026 eVolve MEP, LLC

import React, { type ReactNode, useActionState, useCallback, useEffect, useMemo, useRef } from 'react';

import { type FileId, useEvolveGet, type GetPresignedUrlResponse, type StrongOmit } from '@evolvemep/api-ts';
import { notifications } from '@mantine/notifications';
import { isAfter, parseISO } from 'date-fns';

import { downloadFileResponseWithMarkup } from 'components/Mantine/FileUploads/downloadFileResponseWithMarkup';
import { downloadFileViaLinkElement } from 'components/Mantine/FileUploads/downloadFileViaLinkElement';
import { downloadImageResponseWithMarkup } from 'components/Mantine/FileUploads/downloadImageResponseWithMarkup';
import {
  MARKUP_IMAGE_CONTENT_TYPES,
  type MarkupImageContentType,
} from 'components/Mantine/FileUploads/fileContentTypes';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { useGeneralContext } from 'helpers/useGeneralContext';

type FileDownloadOptions = {
  excludeMarkup?: boolean;
};

const isMarkupImageContentType = (contentType: string): contentType is MarkupImageContentType =>
  MARKUP_IMAGE_CONTENT_TYPES.some((markupImageContentType) => markupImageContentType === contentType);

export type FilesCacheContextType = {
  presignedUrlCache: Record<
    FileId,
    {
      response: GetPresignedUrlResponse;
      expiration: Date;
    }
  >;
  getPresignedUrl: (fileId: FileId, showError?: boolean) => Promise<GetPresignedUrlResponse | null>;
  getPresignedUrls: (fileId: FileId[], showError?: boolean) => Promise<(GetPresignedUrlResponse | null)[]>;
  presigningInProgress: boolean;
  downloadFile: (fileId: FileId, options?: FileDownloadOptions) => Promise<void>;
  /** Updates the cache for a specific file. Used when changes are made, such as adding markup. */
  updateCache: (
    fileId: FileId,
    update: Partial<StrongOmit<GetPresignedUrlResponse, 'error' | 'preSignedUrl' | 'fileId'>>,
  ) => void;
};

const FilesCacheContext = React.createContext<FilesCacheContextType | undefined>(undefined);

export const FilesCacheProvider = ({ children }: { children: ReactNode }) => {
  const [presignedUrlCache, addToCache] = useActionState<
    FilesCacheContextType['presignedUrlCache'],
    GetPresignedUrlResponse
  >(
    (cache, response) => ({
      ...cache,
      [response.fileId]: {
        response,
        expiration: parseISO(response.expires),
      },
    }),
    {},
  );
  const presignedUrlCacheRef = useRef<typeof presignedUrlCache>({});
  useEffect(() => {
    presignedUrlCacheRef.current = presignedUrlCache;
  }, [presignedUrlCache]);

  const { apiCall: getPresignedUrlApiCall, loading: presigningInProgress } = useEvolveGet('shop/file/download');

  const getPresignedUrls = useCallback<FilesCacheContextType['getPresignedUrls']>(
    async (fileIds, showError = true) => {
      const rightNow = new Date();
      const unfetchedAndExpiredFileIds = fileIds.filter((fileId) =>
        isAfter(rightNow, presignedUrlCacheRef.current[fileId]?.expiration ?? new Date(0)),
      );
      const responses =
        unfetchedAndExpiredFileIds.length > 0
          ? await getPresignedUrlApiCall({
              params: {
                fileIds: unfetchedAndExpiredFileIds,
              },
            })
          : [];
      const successResponses = responses.filter((f) => isNil(f.error));
      if (successResponses.length !== responses.length && showError) {
        const errors = responses
          .filter((f) => isNotNil(f.error))
          .map(({ error }) => error)
          .join('\n');
        notifications.show({
          title: 'Download failed',
          message: errors,
          color: 'red',
        });
      }
      successResponses.forEach((response) => addToCache(response));
      return fileIds.map(
        (fileId) =>
          responses.find((response) => response.fileId === fileId) ??
          presignedUrlCacheRef.current[fileId]?.response ??
          null,
      );
    },
    [getPresignedUrlApiCall],
  );

  const getPresignedUrl = useCallback<FilesCacheContextType['getPresignedUrl']>(
    async (fileId: FileId, showError = true) => {
      const res = await getPresignedUrls([fileId], showError);
      return res[0] ?? null;
    },
    [getPresignedUrls],
  );

  const downloadFile = useCallback<FilesCacheContextType['downloadFile']>(
    async (fileId: FileId, { excludeMarkup = false } = {}) => {
      const response = await getPresignedUrl(fileId);
      if (!response) return;

      const markupPages = response.fileMarkup?.markupPages ?? [];
      if (excludeMarkup || markupPages.length === 0) {
        if (response.preSignedUrl) {
          downloadFileViaLinkElement(response.fileName, response.preSignedUrl);
        }
        return;
      }

      const contentType = response.contentType.toLowerCase();
      if (isMarkupImageContentType(contentType) && isNotNil(markupPages[0])) {
        await downloadImageResponseWithMarkup(response, markupPages[0]);
      } else if (contentType === 'application/pdf') {
        await downloadFileResponseWithMarkup(response, markupPages);
      } else if (response.preSignedUrl) {
        downloadFileViaLinkElement(response.fileName, response.preSignedUrl);
      }
    },
    [getPresignedUrl],
  );

  const updateCache = useCallback<FilesCacheContextType['updateCache']>(
    (fileId, update) => {
      const cached = structuredClone(presignedUrlCacheRef.current[fileId]);
      if (cached) {
        addToCache({ ...cached.response, ...update });
      } else {
        void getPresignedUrl(fileId);
      }
    },
    [getPresignedUrl],
  );

  const value = useMemo<FilesCacheContextType>(
    () => ({
      presignedUrlCache,
      getPresignedUrl,
      getPresignedUrls,
      presigningInProgress,
      downloadFile,
      updateCache,
    }),
    [downloadFile, getPresignedUrl, getPresignedUrls, presignedUrlCache, presigningInProgress, updateCache],
  );
  return <FilesCacheContext value={value}>{children}</FilesCacheContext>;
};

export const useFileCache = () => useGeneralContext(FilesCacheContext, 'DocumentsCache');
